(function (root, factory) {
  const api = factory();
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  } else {
    root.Multipart = api;
  }
}(typeof globalThis !== "undefined" ? globalThis : this, () => {
  const decoder = new TextDecoder("ascii");
  const encoder = new TextEncoder();
  const headerEnd = encoder.encode("\r\n\r\n");

  function parseMultipartBoundary(contentType) {
    if (typeof contentType !== "string" || !/^multipart\/x-mixed-replace\s*(?:;|$)/i.test(contentType)) {
      throw new TypeError("response does not contain a multipart boundary");
    }
    const match = /(?:^|;)\s*boundary\s*=\s*(?:"([^"]+)"|([^;\s]+))/i.exec(contentType);
    const boundary = match?.[1] || match?.[2];
    if (!boundary || /[\r\n]/.test(boundary)) throw new TypeError("response does not contain a valid multipart boundary");
    return boundary;
  }

  function concat(left, right) {
    if (!left.length) return Uint8Array.from(right);
    const output = new Uint8Array(left.length + right.length);
    output.set(left);
    output.set(right, left.length);
    return output;
  }

  function indexOf(input, needle) {
    outer: for (let offset = 0; offset <= input.length - needle.length; offset += 1) {
      for (let index = 0; index < needle.length; index += 1) {
        if (input[offset + index] !== needle[index]) continue outer;
      }
      return offset;
    }
    return -1;
  }

  class MultipartParser {
    constructor({ boundary, onPart, maxHeaderBytes = 8192 }) {
      if (typeof boundary !== "string" || !boundary || /[\r\n]/.test(boundary)) throw new TypeError("invalid multipart boundary");
      if (typeof onPart !== "function") throw new TypeError("onPart must be a function");
      this.marker = encoder.encode(`--${boundary}`);
      this.onPart = onPart;
      this.maxHeaderBytes = maxHeaderBytes;
      this.buffer = new Uint8Array();
      this.state = "boundary";
      this.part = null;
      this.closed = false;
    }

    push(chunk) {
      if (this.closed) throw new Error("multipart stream is closed");
      this.buffer = concat(this.buffer, chunk);
      while (this.consume()) {}
    }

    consume() {
      if (this.state === "boundary") {
        if (this.buffer.length >= 2 && this.buffer[0] === 13 && this.buffer[1] === 10) this.buffer = this.buffer.subarray(2);
        if (this.buffer.length < this.marker.length + 2) return false;
        if (indexOf(this.buffer.subarray(0, this.marker.length), this.marker) !== 0) throw new Error("invalid multipart boundary");
        const suffix = this.buffer.subarray(this.marker.length, this.marker.length + 2);
        this.buffer = this.buffer.subarray(this.marker.length + 2);
        if (suffix[0] === 45 && suffix[1] === 45) {
          this.state = "closed";
          return false;
        }
        if (suffix[0] !== 13 || suffix[1] !== 10) throw new Error("invalid multipart boundary delimiter");
        this.state = "headers";
        return true;
      }

      if (this.state === "headers") {
        const end = indexOf(this.buffer, headerEnd);
        if (end < 0) {
          if (this.buffer.length > this.maxHeaderBytes) throw new Error("multipart header is too large");
          return false;
        }
        if (end > this.maxHeaderBytes) throw new Error("multipart header is too large");
        const headers = {};
        for (const line of decoder.decode(this.buffer.subarray(0, end)).split("\r\n")) {
          const separator = line.indexOf(":");
          if (separator <= 0) throw new Error("invalid multipart header");
          headers[line.slice(0, separator).trim().toLowerCase()] = line.slice(separator + 1).trim();
        }
        if (!/^(0|[1-9]\d*)$/.test(headers["content-length"] || "")) throw new Error("invalid multipart Content-Length");
        this.part = {
          contentType: (headers["content-type"] || "application/octet-stream").toLowerCase(),
          contentLength: Number(headers["content-length"]),
          headers,
        };
        if (!Number.isSafeInteger(this.part.contentLength)) throw new Error("invalid multipart Content-Length");
        this.buffer = this.buffer.subarray(end + headerEnd.length);
        this.state = "body";
        return true;
      }

      if (this.state === "body") {
        if (this.buffer.length < this.part.contentLength) return false;
        const body = Uint8Array.from(this.buffer.subarray(0, this.part.contentLength));
        this.buffer = this.buffer.subarray(this.part.contentLength);
        this.onPart({ ...this.part, body });
        this.part = null;
        this.state = "boundary";
        return true;
      }
      return false;
    }

    finish() {
      if (this.state === "body" || this.state === "headers") throw new Error("truncated multipart stream");
      if (this.state === "boundary" && this.buffer.length > 2) throw new Error("truncated multipart boundary");
      if (this.state === "closed" && decoder.decode(this.buffer) !== "\r\n" && this.buffer.length) {
        throw new Error("invalid multipart epilogue");
      }
      this.closed = true;
    }
  }

  return { MultipartParser, parseMultipartBoundary };
}));